'use client'

import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useCallback, useEffect, useMemo, useState } from 'react'

dayjs.extend(customParseFormat)

export interface UseSalePeriodOptions {
  endAt: Date | string | number | Dayjs
  startAt?: Date | string | number | Dayjs
  refreshInterval?: number
}

export interface UseSalePeriodResult {
  isBeforeSale: boolean
  isSaleActive: boolean
  isSaleEnded: boolean
  lastEvaluatedAt: number
}

export const useSalePeriod = ({
  endAt,
  startAt,
  refreshInterval = 30_000,
}: UseSalePeriodOptions): UseSalePeriodResult => {
  const endAtTime = useMemo(() => toTimestamp(endAt), [endAt])
  const startAtTime = useMemo(() => (startAt != null ? toTimestamp(startAt) : undefined), [startAt])

  const computeStatus = useCallback(() => {
    const now = Date.now()
    const isBeforeSale = startAtTime != null ? now < startAtTime : false
    const isSaleEnded = now >= endAtTime

    return {
      isBeforeSale,
      isSaleEnded,
      isSaleActive: !isBeforeSale && !isSaleEnded,
      lastEvaluatedAt: now,
    }
  }, [endAtTime, startAtTime])

  const [status, setStatus] = useState(() => computeStatus())

  useEffect(() => {
    setStatus(computeStatus())
  }, [computeStatus])

  useEffect(() => {
    if (status.isSaleEnded) {
      return
    }

    const intervalId = window.setInterval(() => {
      setStatus(computeStatus())
    }, refreshInterval)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [computeStatus, refreshInterval, status.isSaleEnded])

  return status
}

const toTimestamp = (value: Date | string | number | Dayjs) => {
  if (dayjs.isDayjs(value)) {
    return value.valueOf()
  }

  if (value instanceof Date || typeof value === 'number') {
    const time = new Date(value).getTime()

    if (!Number.isNaN(time)) {
      return time
    }
  }

  if (typeof value === 'string') {
    let parsed = dayjs(value)

    if (!parsed.isValid()) {
      parsed = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true)
    }

    if (parsed.isValid()) {
      return parsed.valueOf()
    }
  }

  throw new Error('useSalePeriod: 유효하지 않은 날짜 값입니다.')
}
